import { renderToString } from "react-dom/server";
import { StaticRouter } from "react-router-dom/server";
import { Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Theme } from "@radix-ui/themes";
import type {
  PrerenderArguments,
  PrerenderResult,
} from "vite-prerender-plugin";
import { HomePage } from "./routes/pages/HomePage";
import { LearnMorePage } from "./routes/pages/LearnMorePage";
import { TermsPage } from "./routes/pages/TermsPage";
import { PrivacyPage } from "./routes/pages/PrivacyPage";
import { SafetyProtocolPage } from "./routes/pages/SafetyProtocolPage";
import { WelcomePage } from "./routes/pages/WelcomePage";

// Minimal app shell — no tracking providers, safe for Node.js SSR
function PrerenderApp() {
  return (
    <Theme
      appearance="light"
      accentColor="gray"
      grayColor="gray"
      radius="large"
    >
      <Routes>
        <Route index element={<HomePage />} />
        <Route path="learn-more" element={<LearnMorePage />} />
        <Route path="terms-conditions" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="safety-protocol" element={<SafetyProtocolPage />} />
        <Route path="welcome" element={<WelcomePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Theme>
  );
}

type RouteSeo = { title: string; description?: string };

const routeSeo: Record<string, RouteSeo> = {
  "/": {
    title: "Iris",
    description:
      "Iris is your non-clinical therapy companion, available 24/7 on iMessage. Experience support that feels like a breeze.",
  },
  "/learn-more": {
    title: "Learn More | Iris",
    description:
      "The story of Iris — a new kind of AI companion designed to help you understand and befriend your mind.",
  },
  "/privacy": {
    title: "Privacy Policy | Iris",
    description:
      "Learn how IrisChat handles your personal information and protects your privacy.",
  },
  "/terms-conditions": {
    title: "Terms & Conditions | Iris",
    description: "Terms of use for the IrisChat AI companion service.",
  },
  "/safety-protocol": {
    title: "Safety Protocol | Iris",
    description:
      "IrisChat's commitment to user safety, crisis response, and mental health support protocols.",
  },
  "/welcome": {
    title: "Access Unlocked! | Iris",
    description:
      "Start your journey with Iris - your AI companion on iMessage.",
  },
};

export async function prerender({
  url,
}: PrerenderArguments): Promise<PrerenderResult> {
  const html = renderToString(
    <HelmetProvider>
      <StaticRouter location={url}>
        <PrerenderApp />
      </StaticRouter>
    </HelmetProvider>,
  );

  const seo = routeSeo[url] ?? routeSeo["/"];
  const canonical = `https://irischat.ai${url === "/" ? "" : url}`;
  const socialImage = "https://irischat.ai/social-card.webp";

  type HeadEl = { type: string; props: Record<string, string> };
  const elementList: HeadEl[] = [
    { type: "meta", props: { property: "og:type", content: "website" } },
    { type: "meta", props: { property: "og:url", content: canonical } },
    { type: "meta", props: { property: "og:title", content: seo.title } },
    { type: "meta", props: { property: "og:image", content: socialImage } },
    { type: "meta", props: { property: "og:image:width", content: "1280" } },
    { type: "meta", props: { property: "og:image:height", content: "832" } },
    {
      type: "meta",
      props: { name: "twitter:card", content: "summary_large_image" },
    },
    { type: "meta", props: { name: "twitter:title", content: seo.title } },
    { type: "meta", props: { name: "twitter:image", content: socialImage } },
    { type: "link", props: { rel: "canonical", href: canonical } },
  ];

  if (seo.description) {
    elementList.push(
      {
        type: "meta",
        props: { name: "description", content: seo.description },
      },
      {
        type: "meta",
        props: { property: "og:description", content: seo.description },
      },
      {
        type: "meta",
        props: { name: "twitter:description", content: seo.description },
      },
    );
  }

  return {
    html,
    links: new Set([
      "/learn-more",
      "/privacy",
      "/terms-conditions",
      "/safety-protocol",
      "/welcome",
    ]),
    head: {
      title: seo.title,
      elements: new Set(elementList),
    },
  };
}
